import type { MatchHubMockData } from '../types/matchHub.types';

export interface RawApiEvent {
  time?: { elapsed?: number; extra?: number | null };
  team?: { id?: number };
  type?: string;
  detail?: string;
  player?: { name?: string | null };
  assist?: { name?: string | null };
  comments?: string | null;
}

export function mapEventsToHub(
  payload: { events: RawApiEvent[] } | null,
  homeExtId: number | null,
): MatchHubMockData['events'] {
  if (!payload?.events?.length) return [];

  const sorted = [...payload.events].sort((a, b) => {
    const aMin = (a.time?.elapsed ?? 0) * 1000 + (a.time?.extra ?? 0);
    const bMin = (b.time?.elapsed ?? 0) * 1000 + (b.time?.extra ?? 0);
    return aMin - bMin;
  });

  const result: MatchHubMockData['events'] = [];
  let homeScore = 0;
  let awayScore = 0;
  let htDividerInserted = false;
  let etDividerInserted = false;

  for (const ev of sorted) {
    const elapsed = ev.time?.elapsed ?? 0;
    const extra = ev.time?.extra ?? null;
    const isSecondHalfOrLater = elapsed > 45 && extra == null;

    if (!htDividerInserted && isSecondHalfOrLater) {
      result.push({
        minute: 45,
        type: 'halftime',
        side: 'neutral',
        text: '',
        halftimeScoreHome: homeScore,
        halftimeScoreAway: awayScore,
      });
      htDividerInserted = true;
    }

    if (!etDividerInserted && elapsed > 90 && extra == null) {
      result.push({ minute: 90, type: 'period_info', side: 'neutral', text: '90 minutes' });
      etDividerInserted = true;
    }

    const type = ev.type;
    if (type === 'Goal') {
      const side = homeExtId != null && ev.team?.id === homeExtId ? 'home' : 'away';
      if (side === 'home') homeScore++;
      else awayScore++;
      result.push({
        minute: elapsed,
        ...(extra != null ? { extraMinute: extra } : {}),
        type: 'goal',
        side,
        text: ev.player?.name ?? '',
        scoreAfter: `${homeScore}-${awayScore}`,
        ...(ev.assist?.name ? { assistLabel: ev.assist.name } : {}),
      });
    } else if (type === 'Card') {
      const side = homeExtId != null && ev.team?.id === homeExtId ? 'home' : 'away';
      const detail = ev.detail ?? '';
      const cardKind: 'yellow' | 'red' =
        detail.toLowerCase().includes('red') ? 'red' : 'yellow';
      result.push({
        minute: elapsed,
        ...(extra != null ? { extraMinute: extra } : {}),
        type: 'card',
        side,
        text: ev.player?.name ?? '',
        cardKind,
      });
    } else if (type === 'subst') {
      const side = homeExtId != null && ev.team?.id === homeExtId ? 'home' : 'away';
      result.push({
        minute: elapsed,
        ...(extra != null ? { extraMinute: extra } : {}),
        type: 'subst',
        side,
        text: '',
        // API-Football: `player` is the one coming OFF, `assist` is the one coming ON.
        playerOn: ev.assist?.name ?? '',
        playerOff: ev.player?.name ?? '',
      });
    } else if (type === 'Var') {
      const side = homeExtId != null && ev.team?.id === homeExtId ? 'home' : 'away';
      result.push({
        minute: elapsed,
        ...(extra != null ? { extraMinute: extra } : {}),
        type: 'var',
        side,
        text: ev.detail ?? ev.comments ?? '',
      });
    }
  }

  return result;
}
